import toLower from "lodash/toLower";

import { WorkflowWebhookParameterType } from "@/enums/workflows";
import {
  PHASE_APPLICATION,
  PHASE_CLOSING,
  PHASE_FUNDED,
  PHASE_UNDERWRITING,
  PRODUCT_TYPE,
  PRODUCT_TYPE_ARLOC,
  PRODUCT_TYPE_EQUIPMENT_FINANCING,
  PRODUCT_TYPE_ERC,
  PRODUCT_TYPE_LINE_OF_CREDIT,
  PRODUCT_TYPE_RECEIVABLE_PURCHASE,
  PRODUCT_TYPE_TERM_LOAN,
} from "@/helpers/constants";

import type { FinProductNum, FinancialProduct } from "@/models/common";
import type {
  ApplicationStage,
  BusinessTransportationEntry,
  EquipmentDetailsEntry,
  FinProduct,
  IWorkflowStatus,
  StageCategoryType,
} from "@/models/workflows";

export const WORKFLOW_SCHEMA_VERSION = "3.46.0" as const;

export const WORKFLOW_BLOCKS_IDS = {
  discovery_questions: "discovery",
  prequalification: "prequal",
  personal_info: "personal",
  personal_tax_return: "personal_tax_return_information",
  business_info: "business",
  business_tax_return: "business_tax_return_information",
  bonding_agent: "bonding_agent",
  insurance: "insurance",
  business_notes: "business_notes",
  additional_business_info: "additional_business",
  funding: "funding",
  company_officers: "company_officers",
  guarantor: "guarantor",
  affiliated_additional_businesses:
    "affiliated_businesses_additional_information",
  affiliated_businesses: "affiliated_businesses",
  additinal_owner: "additional_owner",
  accounts_payable: "accounts_payable",
  signed_application: "signed_application",
  bank_data: "bank_data_plaid",
  affiliated_individual: "affiliated_individuals",
  affiliated_individual_additional:
    "affiliated_individuals_additional_information",
  company_debts: "company_debts",
  trade_references: "trade_references",
  stips: "stips",
  customer_information: "customer_information",
  custom_attributes: "custom_attributes",
  bank_data_moneythumb: "bank_data_moneythumb",
  bank_data_plaid: "bank_data_plaid",
  bank_data_ocrolus: "bank_data_ocrolus",
  equipment_details: "equipment_details",
  business_transportation_information: "business_transportation_information",
  funding_summary: "funding_summary",
  funding_details: "funding_details",
  offer_details: "offer_details",
  arsen_ai_enrich_match: "arsen_ai_enrich_match",
  bank_account_details: "bank_account_details",
  business_debt_schedule: "business_debt_schedule",
  personal_debt_schedule: "personal_debt_schedule",
} as const;

export const WORKFLOW_STAGE_TYPES = {
  UNDERWRITING: "underwriting",
} as const;

export const BUSINESS_INFO_FIELD_IDS = {
  legal_name: "business_legal_name",
  dba: "business_dba",
  stated_monthly_revenue: "business_stated_monthly_revenue",
  entity_type: "business_entity_type",
  start_date: "business_start_date",
  state_of_incorporation: "business_state_of_incorporation",
  ein: "business_ein",
  bn: "business_bn",
  address: "business_addresses",
  phone_numbers: "business_phone_numbers",
} as const;

export const PERSONAL_INFO_FIELD_IDS = {
  address: "personal_addresses",
  credit_permission: "personal_credit_permission",
  drivers_license_number: "personal_drivers_license_number",
  drivers_license_State: "personal_drivers_license_state",
  ein: "personal_ein",
  email: "personal_email_address",
  first_name: "personal_first_name",
  last_name: "personal_last_name",
  ownership_percentage: "personal_ownership_percentage",
  ssn_itin: "personal_ssn_itin",
  sin: "personal_sin",
  telephone: "personal_telephone",
  birth_date: "personal_date_of_birth",
  phone_numbers: "personal_phone_numbers",
} as const;

export const PERSONAL_TAX_RETURN_FIELD_IDS = {
  taxpayer_first_name: "personal_tax_return_information_taxpayer_first_name",
  taxpayer_last_name: "personal_tax_return_information_taxpayer_last_name",
  taxpayer_ssn: "personal_tax_return_information_taxpayer_ssn",
  taxpayer_address_street:
    "personal_tax_return_information_taxpayer_address_street",
  taxpayer_address_city:
    "personal_tax_return_information_taxpayer_address_city",
  taxpayer_address_state:
    "personal_tax_return_information_taxpayer_address_state",
  taxpayer_address_zip_code:
    "personal_tax_return_information_taxpayer_address_zip_code",
  spouse_first_name: "personal_tax_return_information_spouse_first_name",
  spouse_last_name: "personal_tax_return_information_spouse_last_name",
  spouse_ssn: "personal_tax_return_information_spouse_ssn",
  filing_status: "personal_tax_return_information_filing_status",
  dependents: "personal_tax_return_information_dependents",
  income_wages_total: "personal_tax_return_information_income_wages_total",
  income_taxable_interest:
    "personal_tax_return_information_income_taxable_interest",
  income_ordinary_dividends:
    "personal_tax_return_information_income_ordinary_dividends",
  income_qualified_dividends:
    "personal_tax_return_information_income_qualified_dividends",
  income_taxable_ira_distributions:
    "personal_tax_return_information_income_taxable_ira_distributions",
  income_taxable_pensions_annuities:
    "personal_tax_return_information_income_taxable_pensions_annuities",
  income_taxable_social_security:
    "personal_tax_return_information_income_taxable_social_security",
  income_capital_gain_loss:
    "personal_tax_return_information_income_capital_gain_loss",
  income_other_income_loss:
    "personal_tax_return_information_income_other_income_loss",
  income_total_income: "personal_tax_return_information_income_total_income",
  income_adjusted_gross_income:
    "personal_tax_return_information_income_adjusted_gross_income",
  income_standard_deduction:
    "personal_tax_return_information_income_standard_deduction",
  tax_and_credits_child_tax_credit:
    "personal_tax_return_information_tax_and_credits_child_tax_credit",
  tax_and_credits_self_employment_tax:
    "personal_tax_return_information_tax_and_credits_self_employment_tax",
  tax_and_credits_total_tax:
    "personal_tax_return_information_tax_and_credits_total_tax",
  payments_federal_tax_withheld_w2:
    "personal_tax_return_information_payments_federal_tax_withheld_w2",
  payments_federal_tax_withheld_1099:
    "personal_tax_return_information_payments_federal_tax_withheld_1099",
  payments_federal_tax_withheld_other:
    "personal_tax_return_information_payments_federal_tax_withheld_other",
  payments_estimated_tax_payments:
    "personal_tax_return_information_payments_estimated_tax_payments",
  payments_earned_income_credit:
    "personal_tax_return_information_payments_earned_income_credit",
  payments_additional_child_tax_credit:
    "personal_tax_return_information_payments_additional_child_tax_credit",
  payments_american_opportunity_credit:
    "personal_tax_return_information_payments_american_opportunity_credit",
  payments_total_payments:
    "personal_tax_return_information_payments_total_payments",
  refund_amount: "personal_tax_return_information_refund_amount",
  amount_owed: "personal_tax_return_information_amount_owed",
  estimated_tax_penalty:
    "personal_tax_return_information_estimated_tax_penalty",
  refund_amount_applied_to_next_year:
    "personal_tax_return_information_refund_amount_applied_to_next_year",
  third_party_designee_name:
    "personal_tax_return_information_third_party_designee_name",
  third_party_designee_phone:
    "personal_tax_return_information_third_party_designee_phone",
  third_party_designee_pin:
    "personal_tax_return_information_third_party_designee_pin",
  taxpayer_signature_date:
    "personal_tax_return_information_taxpayer_signature_date",
  spouse_signature_date:
    "personal_tax_return_information_spouse_signature_date",
  paid_preparer_name: "personal_tax_return_information_paid_preparer_name",
  paid_preparer_ptin: "personal_tax_return_information_paid_preparer_ptin",
  paid_preparer_self_employed:
    "personal_tax_return_information_paid_preparer_self_employed",
  paid_preparer_firm_name:
    "personal_tax_return_information_paid_preparer_firm_name",
  paid_preparer_phone_number:
    "personal_tax_return_information_paid_preparer_phone_number",
  paid_preparer_firm_ein:
    "personal_tax_return_information_paid_preparer_firm_ein",
  paid_preparer_firm_address_street:
    "personal_tax_return_information_paid_preparer_firm_address_street",
  paid_preparer_firm_address_city:
    "personal_tax_return_information_paid_preparer_firm_address_city",
  paid_preparer_firm_address_state:
    "personal_tax_return_information_paid_preparer_firm_address_state",
  paid_preparer_firm_address_zip_code:
    "personal_tax_return_information_paid_preparer_firm_address_zip_code",
  paid_preparer_signature_date:
    "personal_tax_return_information_paid_preparer_signature_date",
  year: "personal_tax_return_information_year",
};

export const SCHEDULE_C_FIELD_IDS = {
  basic_info_accounting_method: "c_basic_info_accounting_method",
  basic_info_business_name: "c_basic_info_business_name",
  basic_info_business_address_street: "c_basic_info_business_address_street",
  basic_info_business_address_city: "c_basic_info_business_address_city",
  basic_info_business_address_state: "c_basic_info_business_address_state",
  basic_info_business_address_zip_code:
    "c_basic_info_business_address_zip_code",
  basic_info_ein: "c_basic_info_ein",
  basic_info_naics_code: "c_basic_info_naics_code",
  basic_info_profession: "c_basic_info_profession",
  basic_info_proprietor: "c_basic_info_proprietor",
  basic_info_proprietor_material_participant:
    "c_basic_info_proprietor_material_participant",
  basic_info_ssn: "c_basic_info_ssn",
  cogs_closing_inventory_method: "c_cogs_closing_inventory_method",
  cogs_cost_of_goods_sold: "c_cogs_cost_of_goods_sold",
  cogs_cost_of_labor: "c_cogs_cost_of_labor",
  cogs_costs_calculated: "c_cogs_costs_calculated",
  cogs_inventory_at_beginning_of_year: "c_cogs_inventory_at_beginning_of_year",
  cogs_inventory_end_of_year: "c_cogs_inventory_end_of_year",
  cogs_materials_and_supplies: "c_cogs_materials_and_supplies",
  cogs_other_costs: "c_cogs_other_costs",
  cogs_purchases: "c_cogs_purchases",
  expenses_advertising: "c_expenses_advertising",
  expenses_car_and_truck_expenses: "c_expenses_car_and_truck_expenses",
  expenses_comissions_and_fees: "c_expenses_comissions_and_fees",
  expenses_contract_labor: "c_expenses_contract_labor",
  expenses_depletion: "c_expenses_depletion",
  expenses_depreciation_and_expense_deduction:
    "c_expenses_depreciation_and_expense_deduction",
  expenses_employee_benefit_programs: "c_expenses_employee_benefit_programs",
  expenses_energy_efficient_commercial_bldgs_deduction:
    "c_expenses_energy_efficient_commercial_bldgs_deduction",
  expenses_expenses_for_business_use_of_home:
    "c_expenses_expenses_for_business_use_of_home",
  expenses_insurance_other_than_health:
    "c_expenses_insurance_other_than_health",
  expenses_interest_mortgage: "c_expenses_interest_mortgage",
  expenses_interest_other: "c_expenses_interest_other",
  expenses_legal_and_professional_services:
    "c_expenses_legal_and_professional_services",
  expenses_net_profit_or_loss: "c_expenses_net_profit_or_loss",
  expenses_office_expense: "c_expenses_office_expense",
  expenses_other_expenses: "c_expenses_other_expenses",
  expenses_pension_and_profit_sharing_plans:
    "c_expenses_pension_and_profit_sharing_plans",
  expenses_rent_or_lease_other_business_property:
    "c_expenses_rent_or_lease_other_business_property",
  expenses_rent_or_lease_vehicles_machinery_equipment:
    "c_expenses_rent_or_lease_vehicles_machinery_equipment",
  expenses_repairs_and_maintenance: "c_expenses_repairs_and_maintenance",
  expenses_risk_investment_all_investment_is_at_risk:
    "c_expenses_risk_investment_all_investment_is_at_risk",
  expenses_risk_investment_some_investment_is_at_risk:
    "c_expenses_risk_investment_some_investment_is_at_risk",
  expenses_supplies: "c_expenses_supplies",
  expenses_taxes_and_licenses: "c_expenses_taxes_and_licenses",
  expenses_tentative_profits_or_loss: "c_expenses_tentative_profits_or_loss",
  expenses_total_expenses: "c_expenses_total_expenses",
  expenses_travel_meals_deductible_meals:
    "c_expenses_travel_meals_deductible_meals",
  expenses_travel_meals_travel: "c_expenses_travel_meals_travel",
  expenses_utilities: "c_expenses_utilities",
  expenses_wages: "c_expenses_wages",
  income_balance: "c_income_balance",
  income_cost_of_goods_sold: "c_income_cost_of_goods_sold",
  income_gross_income: "c_income_gross_income",
  income_gross_profit: "c_income_gross_profit",
  income_gross_receipts: "c_income_gross_receipts",
  income_other_income_loss: "c_income_other_income_loss",
  income_returns_and_allowances: "c_income_returns_and_allowances",
  other_expenses_details: "c_other_expenses_details",
  vehicle_info_another_vehicle_available_for_personal_use:
    "c_vehicle_info_another_vehicle_available_for_personal_use",
  vehicle_info_evidence_for_deduction_available:
    "c_vehicle_info_evidence_for_deduction_available",
  vehicle_info_evidence_is_written: "c_vehicle_info_evidence_is_written",
  vehicle_info_vehicle_mileage_for_business:
    "c_vehicle_info_vehicle_mileage_for_business",
  vehicle_info_vehicle_mileage_for_commuting:
    "c_vehicle_info_vehicle_mileage_for_commuting",
  vehicle_info_vehicle_mileage_for_other:
    "c_vehicle_info_vehicle_mileage_for_other",
  vehicle_info_vehicle_put_in_service_date:
    "c_vehicle_info_vehicle_put_in_service_date",
  vehicle_info_vehicle_was_available_for_personal_use:
    "c_vehicle_info_vehicle_was_available_for_personal_use",
};

export const SCHEDULE_F_FIELD_IDS = {
  basic_info_accounting_method: "f_basic_info_accounting_method",
  basic_info_activity: "f_basic_info_activity",
  basic_info_ein: "f_basic_info_ein",
  basic_info_made_payments_that_require_1099:
    "f_basic_info_made_payments_that_require_1099",
  basic_info_materially_participated_in_business:
    "f_basic_info_materially_participated_in_business",
  basic_info_part_iv_code: "f_basic_info_part_iv_code",
  basic_info_proprietor_name: "f_basic_info_proprietor_name",
  basic_info_ssn: "f_basic_info_ssn",
  basic_info_will_file_1099s: "f_basic_info_will_file_1099s",
  cash_agricultural_program_payments: "f_cash_agricultural_program_payments",
  cash_agricultural_program_payments_taxable_amount:
    "f_cash_agricultural_program_payments_taxable_amount",
  cash_ccc_loans_forfeited: "f_cash_ccc_loans_forfeited",
  cash_ccc_loans_forfeited_taxable_amount:
    "f_cash_ccc_loans_forfeited_taxable_amount",
  cash_ccc_loans_reported: "f_cash_ccc_loans_reported",
  cash_cooperative_distributions: "f_cash_cooperative_distributions",
  cash_cooperative_distributions_taxable_amount:
    "f_cash_cooperative_distributions_taxable_amount",
  cash_crop_deferred_from_prior_year: "f_cash_crop_deferred_from_prior_year",
  cash_crop_insurance_proceeds_amount_received:
    "f_cash_crop_insurance_proceeds_amount_received",
  cash_and_federal_crop_disaster_payments:
    "f_cash_and_federal_crop_disaster_payments",
  cash_crop_insurance_proceeds_defer_to_next_year:
    "f_cash_crop_insurance_proceeds_defer_to_next_year",
  cash_crop_insurance_proceeds_taxable_amount:
    "f_cash_crop_insurance_proceeds_taxable_amount",
  cash_custom_hire_income: "f_cash_custom_hire_income",
  cash_gross_income: "f_cash_gross_income",
  cash_other_income: "f_cash_other_income",
  cash_purchased_livestock_and_other_items_profit:
    "f_cash_purchased_livestock_and_other_items_profit",
  cash_purchased_livestock_and_other_items_sales:
    "f_cash_purchased_livestock_and_other_items_sales",
  cash_and_other_items_sales: "f_cash_and_other_items_sales",
  cash_sales_of_products: "f_cash_sales_of_products",
  accrual_activity_investment: "f_accrual_activity_investment",
  accrual_car_and_truck_expenses: "f_accrual_car_and_truck_expenses",
  accrual_chemicals: "f_accrual_chemicals",
  accrual_conservation_expenses: "f_accrual_conservation_expenses",
  accrual_custom_hire: "f_accrual_custom_hire",
  accrual_depreciation_expenses: "f_accrual_depreciation_expenses",
  accrual_employee_benefit_programs: "f_accrual_employee_benefit_programs",
  accrual_feed: "f_accrual_feed",
  accrual_fertilizers_and_line: "f_accrual_fertilizers_and_line",
  accrual_freight_and_trucking: "f_accrual_freight_and_trucking",
  accrual_gas_fuel_oil: "f_accrual_gas_fuel_oil",
  accrual_insurance: "f_accrual_insurance",
  accrual_interest: "f_accrual_interest",
  accrual_interest_mortgage: "f_accrual_interest_mortgage",
  accrual_interest_other: "f_accrual_interest_other",
  accrual_labor_hired: "f_accrual_labor_hired",
  accrual_net_farm_profit_or_loss: "f_accrual_net_farm_profit_or_loss",
  accrual_other_expenses: "f_accrual_other_expenses",
  accrual_pension_and_profit_sharing: "f_accrual_pension_and_profit_sharing",
  accrual_rent_or_lease: "f_accrual_rent_or_lease",
  accrual_rent_or_lease_other: "f_accrual_rent_or_lease_other",
  accrual_rent_machinery_equipment: "f_accrual_rent_machinery_equipment",
  accrual_repairs_and_maintenance: "f_accrual_repairs_and_maintenance",
  accrual_seeds_and_plants: "f_accrual_seeds_and_plants",
  accrual_storage_and_warehousing: "f_accrual_storage_and_warehousing",
  accrual_supplies: "f_accrual_supplies",
  accrual_taxes: "f_accrual_taxes",
  accrual_total_expenses: "f_accrual_total_expenses",
  accrual_utilities: "f_accrual_utilities",
  accrual_veterinary_and_medicine: "f_accrual_veterinary_and_medicine",
};

export const BUSINESS_TAX_RETURN_FIELD_IDS = {
  id: "business_tax_return_id",
  business_id: "business_tax_return_business_id",
  year: "business_tax_return_year",
  corporation_name: "business_tax_return_corporation_name",
  employer_identification_number:
    "business_tax_return_employer_identification_number",
  date_incorporated: "business_tax_return_date_incorporated",
  business_activity: "business_tax_return_business_activity",
  product_or_service: "business_tax_return_product_or_service",
  shareholder_name: "business_tax_return_shareholder_name",
  shareholder_identifying_number:
    "business_tax_return_shareholder_identifying_number",
  current_year_allocation_percentage:
    "business_tax_return_current_year_allocation_percentage",
  form_8879_corp_tax_year: "business_tax_return_form_8879_corp_tax_year",
  form_8879_corp_total_income:
    "business_tax_return_form_8879_corp_total_income",
  form_8879_corp_officer_signature_date:
    "business_tax_return_form_8879_corp_officer_signature_date",
  form_8879_corp_ero_signature_date:
    "business_tax_return_form_8879_corp_ero_signature_date",
  form_8879_corp_ero_efin: "business_tax_return_form_8879_corp_ero_efin",
  form_8879_corp_ero_pin: "business_tax_return_form_8879_corp_ero_pin",
  form_1120s_tax_year: "business_tax_return_form_1120s_tax_year",
  form_1120s_selection_effective_date:
    "business_tax_return_form_1120s_selection_effective_date",
  form_1120s_total_assets: "business_tax_return_form_1120s_total_assets",
  form_1120s_gross_receipts_or_sales:
    "business_tax_return_form_1120s_gross_receipts_or_sales",
  form_1120s_cost_of_goods_sold:
    "business_tax_return_form_1120s_cost_of_goods_sold",
  form_1120s_gross_profit: "business_tax_return_form_1120s_gross_profit",
  form_1120s_net_gain_loss_from_form_4797:
    "business_tax_return_form_1120s_net_gain_loss_from_form_4797",
  form_1120s_total_income_loss:
    "business_tax_return_form_1120s_total_income_loss",
  form_1120s_ordinary_business_income_loss:
    "business_tax_return_form_1120s_ordinary_business_income_loss",
  form_1120s_total_deductions:
    "business_tax_return_form_1120s_total_deductions",
  form_1120s_other_deductions:
    "business_tax_return_form_1120s_other_deductions",
  form_1120s_depreciation: "business_tax_return_form_1120s_depreciation",
  form_1120s_rents: "business_tax_return_form_1120s_rents",
  form_1120s_repairs_and_maintenance:
    "business_tax_return_form_1120s_repairs_and_maintenance",
  form_1120s_advertising: "business_tax_return_form_1120s_advertising",
  form_1120s_total_shares_outstanding:
    "business_tax_return_form_1120s_total_shares_outstanding",
  form_1120s_accounting_method:
    "business_tax_return_form_1120s_accounting_method",
  schedule_k1_ordinary_business_income_loss:
    "business_tax_return_schedule_k1_ordinary_business_income_loss",
  schedule_k1_net_section_1231_gain_loss:
    "business_tax_return_schedule_k1_net_section_1231_gain_loss",
  schedule_k1_post_1986_depreciation_adjustment:
    "business_tax_return_schedule_k1_post_1986_depreciation_adjustment",
  schedule_k1_adjusted_gain_or_loss:
    "business_tax_return_schedule_k1_adjusted_gain_or_loss",
  schedule_k1_distributions: "business_tax_return_schedule_k1_distributions",
  schedule_l_cash_beginning_of_year:
    "business_tax_return_schedule_l_cash_beginning_of_year",
  schedule_l_cash_end_of_year:
    "business_tax_return_schedule_l_cash_end_of_year",
  schedule_l_total_assets_beginning_of_year:
    "business_tax_return_schedule_l_total_assets_beginning_of_year",
  schedule_l_total_assets_end_of_year:
    "business_tax_return_schedule_l_total_assets_end_of_year",
  schedule_l_accounts_payable_beginning_of_year:
    "business_tax_return_schedule_l_accounts_payable_beginning_of_year",
  schedule_l_accounts_payable_end_of_year:
    "business_tax_return_schedule_l_accounts_payable_end_of_year",
  schedule_l_total_liabilities_and_equity_beginning_of_year:
    "business_tax_return_schedule_l_total_liabilities_and_equity_beginning_of_year",
  schedule_l_total_liabilities_and_equity_end_of_year:
    "business_tax_return_schedule_l_total_liabilities_and_equity_end_of_year",
  schedule_m1_net_income_loss_per_books:
    "business_tax_return_schedule_m1_net_income_loss_per_books",
  schedule_m1_income_loss_schedule_k:
    "business_tax_return_schedule_m1_income_loss_schedule_k",
  other_deductions_detail: "business_tax_return_other_deductions_detail",
};

export const PREQUAL_INFO_FIELD_IDS = {
  requested_amount: "prequal_requested_amount",
  avg_revenue: "prequal_monthly_avg_revenue",
  time_in_business: "prequal_time_in_business",
  prequal_range: "prequal_prequalification_range",
  credit_score: "prequal_credit_score",
} as const;

export const STAGE_CATEGORY_TYPES: Record<ApplicationStage, StageCategoryType> =
  {
    application: "Collect Data",
    underwriting: "Data Enrichment",
    scorecards: "Scorecards",
    placement: "Placement",
    offer: "Offer",
    closing: "Closing",
    funded: "Complete",
  };

export const INSURANCE_FIELD_IDS = {
  insurance_certificate: "insurance_certificate",
  insurance_address: "insurance_address",
  insurance_type: "insurance_type",
  insurance_company: "insurance_company",
  insurance_email_address: "insurance_email_address",
  insurance_first_name: "insurance_first_name",
  insurance_last_name: "insurance_last_name",
  insurance_has_rental_protection: "insurance_has_rental_protection",
  insurance_telephone: "insurance_telephone",
} as const;

export const BONDING_AGENT_FIELD_IDS = {
  agent_address: "bonding_agent_address",
  agent_email: "bonding_agent_email_address",
  agent_first_name: "bonding_agent_first_name",
  agent_last_name: "bonding_agent_last_name",
  agent_company: "bonding_agent_company_name",
  agent_phone: "bonding_agent_telephone",
} as const;

export const ADDITIONAL_BUSINESS_FIELD_IDS = {
  NAICS_code: "additional_business_NAICS_code",
  experian_bin: "additional_business_experian_bin",
  DUNS_number: "additional_business_duns_number",
  SIC_code: "additional_business_SIC_code",
  business_management_software:
    "additional_business_business_management_software",
  fiscal_year_end: "additional_business_fiscal_year_end",
  industry: "additional_business_industry",
  number_of_employees: "additional_business_number_of_employees",
  sub_industry: "additional_business_sub_industry",
  website: "additional_business_website",
  industry_details: "additional_business_industry_other",
  w2_number_of_employees_2020:
    "additional_business_w2_number_of_employees_2020",
  w2_number_of_employees_2021:
    "additional_business_w2_number_of_employees_2021",
  current_business_debt: "additional_business_current_business_debt",
  custom_field: "additional_business_custom_field",
  stated_average_balance: "additional_business_stated_average_balance",
  external_naics_description: "additional_business_external_NAICS_description",
  external_sic_description: "additional_business_external_SIC_description",
  dot_number: "additional_business_dot_number",
  operating_expense: "additional_business_operating_expense",
  stated_annual_revenue: "additional_business_stated_annual_revenue",
  business_description: "additional_business_business_description",
  business_experience: "additional_business_business_experience",
  incomplete_email: "additional_business_incomplete_email",
  incomplete_phone: "additional_business_incomplete_phone",
  stated_monthly_revenue: "additional_business_stated_monthly_revenue",
  use_of_funds: "additional_business_use_of_funds",
  use_of_funds_description: "additional_business_use_of_funds_description",
  stated_industry: "additional_business_stated_industry",
  written_business_plan: "additional_business_written_business_plan",
  financial_projections: "additional_business_financial_projections",
  generating_revenue: "additional_business_generating_revenue",
  veteran: "additional_business_veteran",
  franchise: "additional_business_franchise",
  stated_number_of_employees: "additional_business_stated_number_of_employees",
  involved_in_bankruptcy: "additional_business_involved_in_bankruptcy",
  baselayer_business_id: "additional_business_baselayer_business_id",
  baselayer_search_id: "additional_business_baselayer_search_id",
} as const;

export const FUNDING_INFO_FIELD_IDS = {
  annual_revenue: "funding_estimated_annual_revenue",
  average_monthly_revenue: "funding_actual_average_monthly_revenue",
  when_funds_needed: "funding_when_funds_needed",
  amount_requested: "funding_amount_requested",
  use_of_funds: "funding_use_of_funds",
  products_requested: "funding_products_requested",
} as const;

export const COMPANY_OFFICERS_FIELD_IDS = {
  address: "company_officers_address",
  country_of_citizenship: "company_officers_country_of_citizenship",
  dob: "company_officers_dob",
  email: "company_officers_email",
  first_name: "company_officers_first_name",
  last_name: "company_officers_last_name",
  officer_title: "company_officers_officer_title",
  ssn: "company_officers_ssn",
  telephone: "company_officers_phone_number",
} as const;

export const GUARANTOR_FIELD_IDS = {
  first_name: "guarantor_first_name",
  last_name: "guarantor_last_name",
  dob: "guarantor_dob",
  ssn: "guarantor_ssn",
  email_address: "guarantor_email_address",
  telephone: "guarantor_telephone",
  address: "guarantor_address",
} as const;

export const AFFILIATED_ADDITIONAL_BUSINESS_INFO_FIELDS_IDS = {
  fiscal_year_end:
    "affiliated_businesses_additional_information_fiscal_year_end",
  industry_type_id: "affiliated_businesses_additional_information_industry",
  sub_industry_type_id:
    "affiliated_businesses_additional_information_sub_industry",
  sic_code: "affiliated_businesses_additional_information_SIC_code",
  naics_code: "affiliated_businesses_additional_information_NAICS_code",
  experian_bin: "affiliated_businesses_additional_information_experian_bin",
  duns_number: "affiliated_businesses_additional_information_duns_number",
  website_address: "affiliated_businesses_additional_information_website",
  number_of_employees_id:
    "affiliated_businesses_additional_information_number_of_employees",
  business_management_software:
    "affiliated_businesses_additional_information_business_management_software",
  industry_details:
    "affiliated_businesses_additional_information_industry_other",
  w2_number_of_employees_2020:
    "affiliated_businesses_additional_information_w2_number_of_employees_2020",
  w2_number_of_employees_2021:
    "affiliated_businesses_additional_information_w2_number_of_employees_2021",
  current_business_debt:
    "affiliated_businesses_additional_information_current_business_debt",
  custom_field: "affiliated_businesses_additional_information_custom_field",
  dot_number: "affiliated_businesses_additional_information_dot_number",
} as const;

export const AFFILITED_BUSINESS_INFO_FIELD_IDS = {
  legal_name: "affiliated_businesses_legal_name",
  dba: "affiliated_businesses_dba",
  stated_monthly_revenue: "affiliated_businesses_stated_monthly_revenue",
  entity_type: "affiliated_businesses_entity_type",
  start_date: "affiliated_businesses_start_date",
  state_of_incorporation: "affiliated_businesses_state_of_incorporation",
  ein: "affiliated_businesses_ein",
  address: "affiliated_businesses_addresses",
  phone_numbers: "affiliated_businesses_phone_numbers",
} as const;

export const WORKFLOW_TO_APP_STATUS = {
  application: 0,
  underwriting: 1,
  placement: 2,
  offer: 3,
  closing: 4,
  funded: 5,
  scorecards: 9,
};
export const ADDITIONAL_OWNER_INFO_FIELD_IDS = {
  actual_credit_score: "additional_owner_actual_credit_score",
  credit_limit: "additional_owner_credit_limit",
  credit_permission: "additional_owner_credit_permission",
  credit_utilization: "additional_owner_credit_utilization",
  inquiries_6_months: "additional_owner_inquiries_6_months",
  legal_immigration_status: "additional_owner_legal_immigration_status",
  personal_income: "additional_owner_personal_income",
  primary_language_spoken: "additional_owner_primary_language_spoken",
  stated_credit_score: "additional_owner_stated_credit_score",
  total_debt: "additional_owner_total_debt",
  country_of_citizenship: "additional_owner_country_of_citizenship",
} as const;

export const ACCOUNTS_PAYABLE_FIELD_IDS = {
  email_address: "accounts_payable_email_address",
  first_name: "accounts_payable_first_name",
  last_name: "accounts_payable_last_name",
  payment_type: "accounts_payable_payment_type",
  phone_number: "accounts_payable_phone_number",
  preferred_contact_method: "accounts_payable_preferred_contact_method",
  preferred_invoicing_method: "accounts_payable_preferred_invoicing_method",
  tax_exempt: "accounts_payable_tax_exempt",
} as const;

export const HIDDEN_WFB_FIELDS = ["personal_is_primary"];

export const STAGE_GROUP_TYPE = {
  WITH_BLOCKS: "with_blocks",
  WITHOUT_BLOCKS: "without_blocks",
};

export const PRODUCT_TYPE_MAPPING: Record<FinProductNum, FinProduct> = {
  1: "arloc",
  2: "line_of_credit",
  3: "receivables_purchase",
  4: "term_loan",
  5: "equipment_financing",
  9: "erc",
};

export const PRODUCT_TYPE_REVERSE_MAPPING: Record<FinProduct, FinProductNum> = {
  arloc: PRODUCT_TYPE_ARLOC,
  line_of_credit: PRODUCT_TYPE_LINE_OF_CREDIT,
  receivables_purchase: PRODUCT_TYPE_RECEIVABLE_PURCHASE,
  term_loan: PRODUCT_TYPE_TERM_LOAN,
  equipment_financing: PRODUCT_TYPE_EQUIPMENT_FINANCING,
  erc: PRODUCT_TYPE_ERC,
};

export const PRODUCT_TYPE_TITLE_MAPPING: Record<FinProduct, FinancialProduct> =
  {
    arloc: PRODUCT_TYPE.ARLOC,
    line_of_credit: PRODUCT_TYPE.LINE_OF_CREDIT,
    receivables_purchase: PRODUCT_TYPE.RECEIVABLES_PURCHASE,
    term_loan: PRODUCT_TYPE.TERM_LOAN,
    equipment_financing: PRODUCT_TYPE.EQUIPMENT_FINANCING,
    erc: PRODUCT_TYPE.ERC,
  };

export const PRODUCT_TYPES_IDS: Record<FinProduct, FinProduct> = {
  arloc: "arloc",
  line_of_credit: "line_of_credit",
  receivables_purchase: "receivables_purchase",
  term_loan: "term_loan",
  equipment_financing: "equipment_financing",
  erc: "erc",
} as const;

export const AFFILIATED_INDIVIDUAL_FIELD_IDS = {
  id: "affiliated_individual_id",
  business_id: "affiliated_business_id",
  first_name: "affiliated_individuals_first_name",
  last_name: "affiliated_individuals_last_name",
  drivers_license_number: "affiliated_individuals_drivers_license_number",
  drivers_license_state: "affiliated_individuals_drivers_license_state",
  date_of_birth: "affiliated_individuals_date_of_birth",
  telephone: "affiliated_individuals_telephone",
  email_address: "affiliated_individuals_email_address",
  ssn_itin: "affiliated_individuals_ssn_itin",
  addresses: "affiliated_individuals_addresses",
} as const;

export const AFFILIATED_INDIVIDUAL_ADDITIONAL_FIELD_IDS = {
  id: "affiliated_individuals_additional_information_id",
  credit_permission:
    "affiliated_individuals_additional_information_credit_permission",
  total_debt: "affiliated_individuals_additional_information_total_debt",
  credit_limit: "affiliated_individuals_additional_information_credit_limit",
  stated_credit_score:
    "affiliated_individuals_additional_information_stated_credit_score",
  actual_credit_score:
    "affiliated_individuals_additional_information_actual_credit_score",
  credit_utilization:
    "affiliated_individuals_additional_information_credit_utilization",
  inquiries_6_months:
    "affiliated_individuals_additional_information_inquiries_6_months",
  personal_income:
    "affiliated_individuals_additional_information_personal_income",
  legal_immigration_status:
    "affiliated_individuals_additional_information_legal_immigration_status",
  primary_language_spoken:
    "affiliated_individuals_additional_information_primary_language_spoken",
} as const;

export const TRADE_REFERENCE_FIELD_IDS = {
  id: "trade_reference_id",
  business_name: "trade_references_business_name",
  business_phone: "trade_references_business_telephone",
  business_email: "trade_references_business_email",
  business_address: "trade_references_business_address",
  business_contact_first_name: "trade_references_business_contact_first_name",
  business_contact_last_name: "trade_references_business_contact_last_name",
  credit_term: "trade_references_credit_terms",
  date_account_opened: "trade_references_date_account_opened",
  open_ar_balance: "trade_references_open_ar_balance",
  past_due_balance: "trade_references_past_due_balance",
  highest_previous_balance: "trade_references_highest_previous_balance",
  days_beyond_terms: "trade_references_days_beyond_terms",
  number_of_late_payments: "trade_references_number_of_late_payments",
  credit_limit: "trade_references_credit_limit",
  number_of_credit_transactions:
    "trade_references_number_of_credit_transactions",
  date_of_last_transaction: "trade_references_date_of_last_transaction",
} as const;

export const COMPANY_DEBTS_FIELDS_IDS = {
  id: "debt_id",
  balance_owned: "company_debts_balance_owned",
  lender_limit: "company_debts_lender_limit",
  lender_name: "company_debts_lender_name",
  maturity_date: "company_debts_maturity_date",
  type_of_debt: "company_debts_type_of_debt",
  term_length: "company_debts_term_length",
} as const;

export const BANK_ACCOUNT_DETAILS_FIELDS_IDS = {
  id: "bank_account_detail_id",
  account_number: "bank_account_details_account_number",
  routing_number: "bank_account_details_routing_number",
  bank_name: "bank_account_details_bank_name",
} as const;

export const CUSTOMER_INFORMATION_FIELDS_IDS = {
  id: "debtor_id",
  business_name: "customer_information_business_name",
  amount: "customer_information_amount",
  payment_term: "customer_information_payment_term",
} as const;

export const STAGE_MANAGEMENT_OPTIONS = {
  1: "Lendflow",
  2: "Client",
} as const;

export const defaultWfbLiveStatuses: IWorkflowStatus[] = [
  {
    name: "Default live status",
    live: true,
    color: "#888e9d",
  },
];

export const defaultWfbDeadStatuses: IWorkflowStatus[] = [
  {
    name: "Default dead status",
    live: false,
  },
];

export const defaultWfbStatuses: IWorkflowStatus[] = [
  ...defaultWfbLiveStatuses,
  ...defaultWfbDeadStatuses,
];

export const MULTI_RECORD_BLOCKS = [
  "additional_owner",
  "affiliated_businesses",
  "affiliated_businesses_additional_information",
  "affiliated_individuals",
  "affiliated_individuals_additional_information",
  "company_debts",
  "company_officers",
  "customer_information",
  "personal",
  "personal_debt_schedule",
  "trade_references",
];

export const PHASES_WITH_TABS = [
  PHASE_APPLICATION,
  PHASE_UNDERWRITING,
  PHASE_CLOSING,
  PHASE_FUNDED,
].map(toLower);

export const EQUIPMENT_DETAILS_FIELDS: EquipmentDetailsEntry[] = [
  {
    id: "equipment_details_equipment_location_address",
    titleKey: "DEALS.EQUIPMENT_DETAILS.ADDRESS",
  },
  {
    id: "equipment_details_model",
    titleKey: "DEALS.EQUIPMENT_DETAILS.MODEL",
  },
  {
    id: "equipment_details_serial_number",
    titleKey: "DEALS.EQUIPMENT_DETAILS.SERIAL_NUMBER",
  },
  {
    id: "equipment_details_cost",
    titleKey: "DEALS.EQUIPMENT_DETAILS.COST",
    currency: true,
  },
  {
    id: "equipment_details_year",
    titleKey: "DEALS.EQUIPMENT_DETAILS.YEAR",
  },
  {
    id: "equipment_details_miles_hours",
    titleKey: "DEALS.EQUIPMENT_DETAILS.MILES_HOURS",
  },
  {
    id: "equipment_details_seller",
    titleKey: "DEALS.EQUIPMENT_DETAILS.SELLER",
  },
  {
    id: "equipment_details_make",
    titleKey: "DEALS.EQUIPMENT_DETAILS.MAKE",
  },
  {
    id: "equipment_details_vendor",
    titleKey: "DEALS.EQUIPMENT_DETAILS.VENDOR",
  },
  {
    id: "equipment_details_equipment_description",
    titleKey: "DEALS.EQUIPMENT_DETAILS.DESCRIPTION",
  },
  {
    id: "equipment_details_url_link",
    titleKey: "DEALS.EQUIPMENT_DETAILS.LINK",
  },
  {
    id: "equipment_details_total_financing_request",
    titleKey: "DEALS.EQUIPMENT_DETAILS.TOTAL_FINANCING_REQUEST",
    currency: true,
  },
  {
    id: "equipment_details_requested_term",
    titleKey: "DEALS.EQUIPMENT_DETAILS.REQUESTED_TERM",
  },
  {
    id: "equipment_details_new_or_used",
    titleKey: "DEALS.EQUIPMENT_DETAILS.NEW_OR_USED",
  },
];

export const BUSINESS_TRANSPORTATION_FIELDS: BusinessTransportationEntry[] = [
  {
    id: "business_transportation_information_authority_status_broker",
    titleKey: "DEALS.BUSINESS_TRANSPORTATION.AUTHORITY_STATUS_BROKER",
  },
  {
    id: "business_transportation_information_authority_status_common",
    titleKey: "DEALS.BUSINESS_TRANSPORTATION.AUTHORITY_STATUS_COMMON",
  },
  {
    id: "business_transportation_information_usdot_number",
    titleKey: "DEALS.BUSINESS_TRANSPORTATION.USDOT_NUMBER",
  },
  {
    id: "business_transportation_information_entity_type",
    titleKey: "DEALS.BUSINESS_TRANSPORTATION.ENTITY_TYPE",
  },
  {
    id: "business_transportation_information_insurance_on_file",
    titleKey: "DEALS.BUSINESS_TRANSPORTATION.INSURANCE_ON_FILE",
  },
  {
    id: "business_transportation_information_insurance_required",
    titleKey: "DEALS.BUSINESS_TRANSPORTATION.INSURANCE_REQUIRED",
  },
  {
    id: "business_transportation_information_power_units",
    titleKey: "DEALS.BUSINESS_TRANSPORTATION.POWER_UNITS",
  },
  {
    id: "business_transportation_information_usdot_status",
    titleKey: "DEALS.BUSINESS_TRANSPORTATION.USDOT_STATUS",
  },
  {
    id: "business_transportation_information_cargo_carried",
    titleKey: "DEALS.BUSINESS_TRANSPORTATION.CARGO_CARRIED",
    dataType: "array",
  },
  {
    id: "business_transportation_information_operation_classification",
    titleKey: "DEALS.BUSINESS_TRANSPORTATION.OPERATION_CLASSIFICATION",
    dataType: "array",
  },
];

export const CUSTOM_WEBHOOK_TYPE = "custom_webhook" as const;

export const DEFAULT_CUSTOM_WEBHOOK_PARAMETER = {
  name: "",
  type: WorkflowWebhookParameterType.String,
  value: "",
};

export const DEFAULT_CUSTOM_WEBHOOK = {
  name: "",
  description: null,
  type: CUSTOM_WEBHOOK_TYPE,
  enabled: false,
  urls: [""],
  parameters: [],
};

export const CUSTOM_WEBHOOK_SECTION_NAME = "Custom Webhooks" as const;

/**
 * Some blocks can have values belonging to different owners,
 * however, the key used to identify the owner is not consistant.
 * This map can be used to get the key that identifies the owner
 * for a block.
 */
export const BLOCK_TO_OWNER_ID_KEY: Record<string, string> = {
  additional_owner: "additional_owner_id",
  personal: "personal_information_id",
  personal_debt_schedule: "personal_information_id",
} as const;
